import {
  nextTick,
  onScopeDispose,
  ref,
  shallowRef,
  watch,
  type ComputedRef,
} from 'vue';
import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import { createPanoramicRenderImage } from '@/src/panoramic/renderImage';

export function usePanoramicRenderImage(
  source: ComputedRef<vtkImageData | null | undefined>,
  loading: ComputedRef<boolean>,
  getTextureLimit: () => number
) {
  const renderImage = shallowRef<vtkImageData | null>(null);
  const preparing = ref(false);
  const error = ref('');
  watch(
    [source, loading],
    async ([volume, isLoading], _old, onCleanup) => {
      renderImage.value = null;
      error.value = '';
      preparing.value = false;
      if (!volume || isLoading) return;
      const controller = new AbortController();
      let owned: vtkImageData | null = null;
      onCleanup(() => {
        controller.abort();
        // Detach the panes' actors before deleting their shared input image.
        if (owned) void nextTick(() => owned?.delete());
      });
      preparing.value = true;
      try {
        const image = await createPanoramicRenderImage(
          volume,
          getTextureLimit(),
          controller.signal
        );
        if (controller.signal.aborted) {
          if (image !== volume) image.delete();
          return;
        }
        if (image !== volume) owned = image;
        renderImage.value = image;
      } catch (reason) {
        if (!controller.signal.aborted)
          error.value =
            reason instanceof Error
              ? reason.message
              : 'No se pudo preparar la panorámica.';
      } finally {
        if (!controller.signal.aborted) preparing.value = false;
      }
    },
    { immediate: true }
  );
  onScopeDispose(() => {
    renderImage.value = null;
  });
  return { renderImage, preparing, error };
}
